/**
 * Warehouse inventory cookies (IM-002)
 *
 * Persists resolved inventory list id + minimal delivery context for follow-on tickets (IM-004).
 * Values align with Store.c_inventoryListId from Shopper Stores getStores.
 */

/** Dispatched on `window` after inventory cookies are written (IM-003 / IM-004 integration hook). */
export const INVENTORY_CONTEXT_CHANGED_EVENT = 'kl:inventory-context-changed';

const COOKIE_TTL_DAYS = 7;

/**
 * @param {string} siteId
 * @returns {string}
 */
export function getWarehouseInventoryListCookieName(siteId) {
  return `kl_wh_inv_${siteId}`;
}

/**
 * @param {string} siteId
 * @returns {string}
 */
export function getWarehouseDeliveryContextCookieName(siteId) {
  return `kl_wh_addr_${siteId}`;
}

/**
 * Strip PII — keep fields useful for cache keys / re-fetch (TSD delivery context).
 *
 * @param {Object|null|undefined} address
 * @returns {Object|null}
 */
export function sanitizeDeliveryAddressForCookie(address) {
  if (!address || typeof address !== 'object') {
    return null;
  }

  return {
    postalCode: address.postalCode ?? address.postcode ?? '',
    stateCode: address.stateCode ?? '',
    countryCode: address.countryCode ?? '',
  };
}

/**
 * IM-001 gate: preference is non-empty string.
 *
 * @param {Object} preferences - site.preferences from MRT / useMultiSite
 * @returns {boolean}
 */
export function isWarehouseResolutionPreferenceEnabled(preferences) {
  const raw = preferences?.warehouseInventories;

  return raw != null && String(raw).trim() !== '';
}

/**
 * If preference lists explicit ids, resolved list id must be included.
 *
 * @param {Object} preferences
 * @param {string} inventoryListId
 * @returns {boolean}
 */
export function isInventoryListIdAllowedByPreference(preferences, inventoryListId) {
  if (!inventoryListId) {
    return false;
  }

  const raw = preferences?.warehouseInventories;

  if (raw == null || String(raw).trim() === '') {
    return false;
  }

  const allowed = String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (allowed.length === 0) {
    return true;
  }

  return allowed.includes(inventoryListId);
}

/** SCAPI Shopper Products: max inventory list ids per request (TSD IM-004). */
export const MAX_SHOPPER_INVENTORY_IDS = 5;

/**
 * Read a single cookie value from a raw `Cookie` header (SSR).
 *
 * @param {string} cookieHeader
 * @param {string} name - Cookie name (exact match)
 * @returns {string|null}
 */
export function parseCookieValueFromHeader(cookieHeader, name) {
  if (!cookieHeader || !name) {
    return null;
  }

  const parts = cookieHeader.split(/;\s*/);
  const prefix = `${name}=`;

  for (const part of parts) {
    if (part.startsWith(prefix)) {
      try {
        return decodeURIComponent(part.slice(prefix.length));
      } catch {
        return null;
      }
    }
  }

  return null;
}

/**
 * @param {string|null|undefined} val - Raw cookie payload
 * @returns {string|null} Null when absent or sentinel strings
 */
export function normalizeWarehouseInventoryCookieValue(val) {
  if (val == null) {
    return null;
  }

  const s = String(val).trim();

  if (s === '' || s === 'null' || s === 'undefined') {
    return null;
  }

  return s;
}

/**
 * Comma-separated inventory list ids, capped for SCAPI.
 *
 * @param {string|null|undefined} raw
 * @returns {string|undefined} Undefined when nothing to send
 */
export function formatInventoryIdsParam(raw) {
  const normalized = normalizeWarehouseInventoryCookieValue(raw);

  if (!normalized) {
    return undefined;
  }

  const ids = normalized
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, MAX_SHOPPER_INVENTORY_IDS);

  if (ids.length === 0) {
    return undefined;
  }

  return ids.join(',');
}

/**
 * Resolves the `inventoryIds` query value for Shopper Products when warehouse flows are enabled (IM-004).
 *
 * @param {Object} args
 * @param {string} args.siteId
 * @param {Object} [args.preferences] - site.preferences
 * @param {import('http').IncomingMessage|null} [args.req] - SSR request (optional)
 * @returns {string|undefined}
 */
export function resolveShopperInventoryIdsParameter({siteId, preferences, req = null}) {
  if (!isWarehouseResolutionPreferenceEnabled(preferences)) {
    return undefined;
  }

  const raw = getWarehouseInventoryListIdFromCookie(siteId, req);

  if (!raw) {
    return undefined;
  }

  const ids = raw.split(',').map((x) => x.trim()).filter(Boolean);

  if (ids.length === 0) {
    return undefined;
  }

  const filtered = ids.filter((id) => isInventoryListIdAllowedByPreference(preferences, id));

  if (filtered.length === 0) {
    return undefined;
  }

  return formatInventoryIdsParam(filtered.join(','));
}

/**
 * @param {string} siteId
 * @param {import('http').IncomingMessage|null} [req] - When set (SSR), reads `Cookie` header
 * @returns {string|null}
 */
export function getWarehouseInventoryListIdFromCookie(siteId, req = null) {
  const name = getWarehouseInventoryListCookieName(siteId);
  let decoded = null;

  if (req?.headers?.cookie) {
    decoded = parseCookieValueFromHeader(req.headers.cookie, name);
  } else if (typeof document !== 'undefined') {
    const row = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`));

    if (row) {
      try {
        decoded = decodeURIComponent(row.split('=').slice(1).join('='));
      } catch {
        decoded = null;
      }
    }
  }

  return normalizeWarehouseInventoryCookieValue(decoded);
}

/**
 * @param {string} siteId
 * @returns {Object|null}
 */
/**
 * Compare sanitized delivery contexts (postal / state / country only).
 *
 * @param {Object|null|undefined} cookieContext - Value from `kl_wh_addr_*` cookie
 * @param {Object|null|undefined} basketAddress - Basket shipment shipping address
 * @returns {boolean}
 */
export function deliveryContextsMatch(cookieContext, basketAddress) {
  const fromCookie = sanitizeDeliveryAddressForCookie(cookieContext);
  const fromBasket = sanitizeDeliveryAddressForCookie(basketAddress);

  if (!fromCookie || !fromBasket) {
    return false;
  }

  return (
    fromCookie.postalCode === fromBasket.postalCode &&
    fromCookie.stateCode === fromBasket.stateCode &&
    fromCookie.countryCode === fromBasket.countryCode
  );
}

/**
 * True when warehouse cookies reflect a different delivery context than checkout.
 *
 * @param {string} siteId
 * @param {Object|null|undefined} basketShippingAddress
 * @returns {boolean}
 */
export function isCheckoutInventoryContextStale(siteId, basketShippingAddress) {
  const basketCtx = sanitizeDeliveryAddressForCookie(basketShippingAddress);

  if (!basketCtx?.postalCode || !basketCtx?.countryCode) {
    return false;
  }

  const cookieAddr = getWarehouseDeliveryContextFromCookie(siteId);
  const hasInventoryList = Boolean(getWarehouseInventoryListIdFromCookie(siteId));

  if (!cookieAddr && !hasInventoryList) {
    return false;
  }

  return !deliveryContextsMatch(cookieAddr, basketShippingAddress);
}

export function getWarehouseDeliveryContextFromCookie(siteId) {
  if (typeof document === 'undefined') {
    return null;
  }

  const name = getWarehouseDeliveryContextCookieName(siteId);
  const row = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`));

  if (!row) {
    return null;
  }

  try {
    const raw = decodeURIComponent(row.split('=').slice(1).join('='));

    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {string} siteId
 * @param {string} inventoryListId - Store c_inventoryListId
 * @param {Object|null} address - sanitized or raw; will be sanitized internally
 */
export function setWarehouseInventoryCookies(siteId, inventoryListId, address) {
  if (typeof document === 'undefined' || !siteId || !inventoryListId) {
    return;
  }

  const expires = new Date();

  expires.setDate(expires.getDate() + COOKIE_TTL_DAYS);

  const exp = expires.toUTCString();
  const listName = getWarehouseInventoryListCookieName(siteId);
  const addrName = getWarehouseDeliveryContextCookieName(siteId);
  const safeAddr = sanitizeDeliveryAddressForCookie(address);

  const listVal = encodeURIComponent(inventoryListId);
  const addrVal = encodeURIComponent(JSON.stringify(safeAddr || {}));

  document.cookie = `${listName}=${listVal}; expires=${exp}; path=/; SameSite=Lax`;
  document.cookie = `${addrName}=${addrVal}; expires=${exp}; path=/; SameSite=Lax`;

  window.dispatchEvent(
    new CustomEvent(INVENTORY_CONTEXT_CHANGED_EVENT, {detail: {siteId}})
  );
}
